<template>
  <div class="p-6">
    <div v-if="section.tab_contents?.['template-guide']" class="prose max-w-none">
      <!-- Action Bar: Button to preview template -->
      <div class="mb-6 p-4 border rounded-lg flex items-center justify-between"
           style="background: var(--sage-green-light, #E8EEE8); border-color: var(--border-light);">
        <div>
          <h3 class="text-sm font-semibold mb-1" style="color: var(--text-primary);">Policy Template Available</h3>
          <p class="text-sm" style="color: var(--text-secondary);">
            View the actual policy template structure alongside these instructions
          </p>
        </div>
        <Button
          label="Preview Template"
          icon="pi pi-eye"
          severity="secondary"
          @click="showTemplate"
        />
      </div>

      <!-- Template Guide Content -->
      <div v-html="renderedContent"></div>
    </div>

    <div v-else class="text-center py-12" style="color: var(--text-muted);">
      <i class="pi pi-file-edit text-4xl mb-4"></i>
      <p>Template guide content not available</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';
import { useMarkdown } from '@/composables/useMarkdown';
import type { Section } from '@/types/ashmatics';

const props = defineProps<{
  section: Section;
}>();

const emit = defineEmits<{
  'show-template': [];
}>();

const { renderMarkdown } = useMarkdown();

const renderedContent = computed(() => {
  const content = props.section.tab_contents?.['template-guide'];
  return content ? renderMarkdown(content) : '';
});

const showTemplate = () => {
  emit('show-template');
};
</script>

<style scoped>
.prose {
  line-height: 1.7;
}

.prose :deep(h1) {
  font-size: 1.875rem;
  font-weight: 700;
  margin-top: 2rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
}

.prose :deep(h2) {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
}

.prose :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  color: var(--text-secondary);
}

.prose :deep(p) {
  margin-bottom: 1rem;
  color: var(--text-primary);
}

.prose :deep(ul), .prose :deep(ol) {
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

.prose :deep(li) {
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.prose :deep(code) {
  background: var(--background-secondary);
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  color: var(--primary-gold);
}

.prose :deep(pre) {
  background: var(--background-secondary);
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin-bottom: 1rem;
}
</style>
